import { useCallback } from 'react';
import { CapTableInfo } from 'common/types/capTable';
import { ReverseParsedFundOwnershipDetail, SharesAcquisition } from 'pages/CapTable/fund-ownership/types';
import { getNumberValue } from 'utilities';
import { SpreadsheetConfig } from '../../../components/ScalarSpreadsheet/utilities/SpreadsheetConfig';

// setColumns calls are actually calls to setVisibleColumns,
// so these modify state and UI in the most minimal way possible
// these actions will cause the reverseParser to be called with spreadsheet.columns as source of truth for amount of columns
// and write to tableData.fundOwnership.fund_ownership_detail
const useFundOwnershipActions = (spreadsheet: SpreadsheetConfig | undefined) => {
  const deleteColumn = useCallback(
    columnIndex => {
      const sheetData = spreadsheet?.tableData;
      const visibleFods: ReverseParsedFundOwnershipDetail[] = sheetData.fundOwnership.fund_ownership_detail;
      // this includes the persisted fods, deliberately not modified by the logic
      const { fund_ownership } = sheetData.capTable as CapTableInfo;

      if (fund_ownership) {
        const columnToDelete = visibleFods[columnIndex];
        const persistedFod = fund_ownership.fund_ownership_detail.find(item => item.id === columnToDelete.id);

        if (persistedFod) {
          spreadsheet?.tableData.fundOwnership.deleted_fund_ownership_details.push(getNumberValue(persistedFod.id));
        }
        spreadsheet?.setColumns?.(visibleFods.filter(fod => fod.columnRef !== columnToDelete.columnRef));
      }
    },
    [spreadsheet]
  );

  const cloneColumn = useCallback(
    columnIndex => {
      const sheetData = spreadsheet?.tableData;
      const column = sheetData?.fundOwnership.fund_ownership_detail[columnIndex];
      const clonedColumn = { ...column, id: 0 };

      // Reset ids for acquisitions and sales to ensure new entries are created
      clonedColumn.acquisitions = column.acquisitions.map((acquisition: SharesAcquisition) => ({
        ...acquisition,
        sales: (acquisition.sales ?? []).map(sale => ({ ...sale, id: 0 })),
        conversions: (acquisition.conversions ?? []).map(conversion => ({ ...conversion, id: 0 })),
        cash_distributions: (acquisition.cash_distributions ?? []).map(cashDistribution => ({
          ...cashDistribution,
          id: 0,
        })),
        id: 0,
      }));

      const allColumns = [clonedColumn, ...sheetData.fundOwnership.fund_ownership_detail];
      sheetData.columns = allColumns;
      spreadsheet?.setColumns?.(allColumns);
    },
    [spreadsheet]
  );

  const addColumn = useCallback(
    (isCustomSecurity = false) => {
      const sheetData = spreadsheet?.tableData;
      const emptyColumn = {
        id: 0,
        identifier: null,
        fund_ownership: spreadsheet?.tableData.fundOwnership.id ?? 0,
        invested_capital: '0.00',
        fund: null,
        security: null,
        is_deleted: false,
        is_custom_security: isCustomSecurity,
        acquisitions: [],
        order: sheetData.fundOwnership.fund_ownership_detail.length, // order starts at 0
        ...(isCustomSecurity ? { custom_security_name: '' } : null),
      };
      const allColumns = [...sheetData.fundOwnership.fund_ownership_detail, emptyColumn];
      sheetData.columns.push(emptyColumn);
      spreadsheet?.setColumns?.(allColumns);
    },
    [spreadsheet]
  );
  return { cloneColumn, deleteColumn, addColumn };
};

export default useFundOwnershipActions;
